import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiIntegrationsShellyHardware,
  useGetApiIntegrationsShellyDevices,
  useGetApiIntegrationsShellySettings,
  usePutApiIntegrationsShellySettings,
  usePostApiIntegrationsShellyHardware,
  usePutApiIntegrationsShellyHardwareId,
  useDeleteApiIntegrationsShellyHardwareId,
  usePostApiIntegrationsShellyHardwareIdTest,
  usePostApiIntegrationsShellyHardwareIdWebhookConfigure,
  usePostApiIntegrationsShellyHardwareIdWebhookRepair,
  useGetApiIntegrationsShellyHardwareIdDevices,
  useGetApiIntegrationsShellyHardwareIdCapabilities,
  useGetApiIntegrationsShellyHardwareIdWebhookStatus,
  usePostApiIntegrationsShellyDevices,
  useDeleteApiIntegrationsShellyDevicesId,
  getGetApiIntegrationsShellyHardwareQueryKey,
  getGetApiIntegrationsShellyHardwareIdDevicesQueryKey,
  getGetApiIntegrationsShellyDevicesQueryKey,
  getGetApiIntegrationsShellySettingsQueryKey,
  ShellyCreateDeviceRequestDeviceType,
  ShellyCreateDeviceRequestChannelType,
  type DevicesDeviceType,
  type ShellyHardwareResponse,
  type ShellyDeviceResponse,
  type ShellyChannelCapability,
  type ShellyWebhookInfo,
  type ShellyExpectedWebhookStatus,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Check,
  AlertCircle,
  ArrowLeft,
  Plus,
  Server,
  Power,
  Pencil,
  Trash2,
  ChevronRight,
  Wifi,
  Webhook,
  Wrench,
  Settings,
  Copy,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  Button,
  Modal,
  SlideOutPanel,
  TextField,
  PasswordField,
  Checkbox,
  cn,
} from '@/components/ui';
import { getDeviceTypeLabel } from '@/features/devices';

const addHardwareSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  host: z.string().min(1, 'IP address is required').ip({ message: 'Must be a valid IP address' }),
  port: z.coerce
    .number({ invalid_type_error: 'Port must be a number' })
    .min(1, 'Port must be at least 1')
    .max(65535, 'Port must be at most 65535')
    .optional(),
  username: z.string().max(255).optional(),
  password: z.string().max(255).optional(),
  webhookEnabled: z.boolean().optional(),
});

const editHardwareSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  host: z.string().min(1, 'IP address is required').ip({ message: 'Must be a valid IP address' }),
  port: z.coerce
    .number({ invalid_type_error: 'Port must be a number' })
    .min(1, 'Port must be at least 1')
    .max(65535, 'Port must be at most 65535')
    .optional(),
  username: z.string().max(255).optional(),
  password: z.string().max(255).optional(),
  webhookEnabled: z.boolean().optional(),
});

const addDeviceSchema = z.object({
  id: z
    .string()
    .min(1, 'ID is required')
    .max(255)
    .regex(/^[a-z0-9.-]+$/, 'ID must be lowercase letters, numbers, hyphens, and periods only'),
  name: z.string().min(1, 'Name is required').max(255),
  deviceType: z.string().min(1, 'Device type is required'),
  channelType: z.string().min(1, 'Channel type is required'),
  channelIndex: z.coerce.number().min(0).optional(),
});

type AddHardwareFormData = z.infer<typeof addHardwareSchema>;
type EditHardwareFormData = z.infer<typeof editHardwareSchema>;
type AddDeviceFormData = z.infer<typeof addDeviceSchema>;

type TabType = 'hardware' | 'devices' | 'settings';

export function ShellySettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiIntegrationsShellyHardware();

  const [activeTab, setActiveTab] = useState<TabType>('hardware');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHardware, setEditingHardware] = useState<ShellyHardwareResponse | null>(null);
  const [deletingHardware, setDeletingHardware] = useState<ShellyHardwareResponse | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [expandedHardware, setExpandedHardware] = useState<Set<string>>(new Set());
  const [webhookDetailsHardware, setWebhookDetailsHardware] =
    useState<ShellyHardwareResponse | null>(null);

  const addMutation = usePostApiIntegrationsShellyHardware();
  const updateMutation = usePutApiIntegrationsShellyHardwareId();
  const deleteMutation = useDeleteApiIntegrationsShellyHardwareId();

  const hardware = data?.hardware ?? [];

  const invalidateHardware = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsShellyHardwareQueryKey() });
  };

  const toggleHardwareExpanded = (hardwareId: string) => {
    setExpandedHardware((prev) => {
      const next = new Set(prev);
      if (next.has(hardwareId)) {
        next.delete(hardwareId);
      } else {
        next.add(hardwareId);
      }
      return next;
    });
  };

  const navigateToHardware = (hardwareId: string) => {
    setActiveTab('hardware');
    setExpandedHardware((prev) => new Set(prev).add(hardwareId));
  };

  return (
    <div className="space-y-6">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Shelly
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage your Shelly hardware and devices
            </p>
          </div>
          {activeTab === 'hardware' && (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
              Add Hardware
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-white/[0.06]">
        <nav className="-mb-px flex gap-6">
          {(['hardware', 'devices', 'settings'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Hardware Tab */}
      {activeTab === 'hardware' && (
        <>
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
            </div>
          )}

          {/* Error state */}
          {isError && (
            <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="font-medium text-red-400">Unable to fetch hardware</h3>
                  <p className="mt-1 text-sm text-red-400/70">
                    There was an error loading your Shelly hardware list.
                  </p>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
              >
                Retry
              </button>
            </div>
          )}

          {/* Hardware List */}
          {!isLoading && !isError && (
            <>
              {hardware.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
                  <Server className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    No hardware configured
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                    Add your first Shelly device to get started
                  </p>
                  <Button
                    className="mt-6"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => setShowAddModal(true)}
                  >
                    Add Hardware
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {hardware.map((hw) => (
                    <HardwareCard
                      key={hw.id}
                      hardware={hw}
                      isExpanded={expandedHardware.has(hw.id ?? '')}
                      onToggleExpand={() => hw.id && toggleHardwareExpanded(hw.id)}
                      onEdit={() => setEditingHardware(hw)}
                      onDelete={() => setDeletingHardware(hw)}
                      onShowWebhookDetails={() => setWebhookDetailsHardware(hw)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Devices Tab */}
      {activeTab === 'devices' && (
        <DevicesTab hardware={hardware} onNavigateToHardware={navigateToHardware} />
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && <SettingsTab />}

      {/* Add Hardware Modal */}
      <AddHardwareModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            await addMutation.mutateAsync({ data });
            setShowAddModal(false);
            invalidateHardware();
          } catch (err) {
            const error = err as { code?: string; message?: string };
            if (error.code === 'HARDWARE_UNREACHABLE') {
              setAddError(
                'Could not connect to the device. Please verify the IP address and port are correct and the device is powered on.'
              );
            } else if (error.code === 'HARDWARE_ALREADY_EXISTS') {
              setAddError(error.message ?? 'A device with this configuration already exists.');
            } else {
              setAddError('Failed to add hardware. Please try again.');
            }
          }
        }}
        isLoading={addMutation.isPending}
        error={addError}
      />

      {/* Edit Hardware Modal */}
      {editingHardware && (
        <EditHardwareModal
          isOpen={true}
          hardware={editingHardware}
          onClose={() => setEditingHardware(null)}
          onSubmit={async (data) => {
            if (!editingHardware.id) return;
            try {
              await updateMutation.mutateAsync({ id: editingHardware.id, data });
              setEditingHardware(null);
              invalidateHardware();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingHardware && (
        <DeleteHardwareModal
          isOpen={true}
          hardware={deletingHardware}
          onClose={() => setDeletingHardware(null)}
          onConfirm={async () => {
            if (!deletingHardware.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingHardware.id });
              setDeletingHardware(null);
              invalidateHardware();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Webhook Details Panel */}
      <WebhookDetailsPanel
        hardware={webhookDetailsHardware}
        onClose={() => setWebhookDetailsHardware(null)}
      />
    </div>
  );
}

interface HardwareCardProps {
  hardware: ShellyHardwareResponse;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onShowWebhookDetails: () => void;
}

function HardwareCard({
  hardware,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onShowWebhookDetails,
}: HardwareCardProps) {
  const queryClient = useQueryClient();
  const testMutation = usePostApiIntegrationsShellyHardwareIdTest();
  const configureWebhookMutation = usePostApiIntegrationsShellyHardwareIdWebhookConfigure();
  const repairWebhookMutation = usePostApiIntegrationsShellyHardwareIdWebhookRepair();

  const { data: devicesData, isLoading: devicesLoading } =
    useGetApiIntegrationsShellyHardwareIdDevices(hardware.id ?? '', {
      query: { enabled: !!hardware.id },
    });

  const [addingDevice, setAddingDevice] = useState(false);
  const [disablingDevice, setDisablingDevice] = useState<ShellyDeviceResponse | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const devices = devicesData?.devices ?? [];

  const invalidateDevices = () => {
    if (hardware.id) {
      queryClient.invalidateQueries({
        queryKey: getGetApiIntegrationsShellyHardwareIdDevicesQueryKey(hardware.id),
      });
    }
  };

  const handleTest = async () => {
    if (!hardware.id) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({ id: hardware.id });
      setTestResult({
        success: result.connected ?? false,
        message: result.connected
          ? 'Connected successfully'
          : (result.message ?? 'Connection failed'),
      });
    } catch {
      setTestResult({ success: false, message: 'Connection test failed' });
    }
  };

  const handleConfigureWebhook = async () => {
    if (!hardware.id) return;
    try {
      await configureWebhookMutation.mutateAsync({ id: hardware.id });
      queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsShellyHardwareQueryKey() });
    } catch {
      // Error handling done by mutation
    }
  };

  const handleRepairWebhook = async () => {
    if (!hardware.id) return;
    try {
      await repairWebhookMutation.mutateAsync({ id: hardware.id });
      queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsShellyHardwareQueryKey() });
    } catch {
      // Error handling done by mutation
    }
  };

  const getStatusColor = () => {
    if (hardware.status?.available) return 'bg-emerald-400 shadow-lg shadow-emerald-400/30';
    return 'bg-gray-400 dark:bg-zinc-600';
  };

  const getStatusText = () => {
    if (hardware.status?.available) return 'Online';
    if (hardware.status?.error) return 'Error';
    return 'Offline';
  };

  const getWebhookStatus = () => {
    if (!hardware.webhookEnabled)
      return { text: 'Disabled', color: 'text-gray-400 dark:text-zinc-600' };
    if (!hardware.status?.webhookConfigured)
      return { text: 'Not configured', color: 'text-amber-500' };
    if (!hardware.status?.webhookWorking) return { text: 'Needs repair', color: 'text-amber-500' };
    return { text: 'Active', color: 'text-emerald-500' };
  };

  const webhookStatus = getWebhookStatus();

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      {/* Header: Name + Model + Actions */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`mt-1.5 h-3 w-3 flex-shrink-0 rounded-full ${getStatusColor()}`} />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{hardware.name}</h3>
              {hardware.deviceModel && (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                  {hardware.deviceModel}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTest}
              disabled={testMutation.isPending}
              title="Test connection"
            >
              {testMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              ) : (
                <Wifi className="h-4 w-4" />
              )}
            </Button>
            {hardware.webhookEnabled && !hardware.status?.webhookConfigured && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleConfigureWebhook}
                disabled={configureWebhookMutation.isPending}
                title="Configure webhook"
              >
                {configureWebhookMutation.isPending ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                ) : (
                  <Webhook className="h-4 w-4 text-amber-500" />
                )}
              </Button>
            )}
            {hardware.webhookEnabled &&
              hardware.status?.webhookConfigured &&
              !hardware.status?.webhookWorking && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRepairWebhook}
                  disabled={repairWebhookMutation.isPending}
                  title="Repair webhook"
                >
                  {repairWebhookMutation.isPending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                  ) : (
                    <Wrench className="h-4 w-4 text-amber-500" />
                  )}
                </Button>
              )}
            <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} title="Delete">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <div
            className={`mt-3 rounded-lg px-3 py-2 text-sm ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
            }`}
          >
            {testResult.message}
          </div>
        )}

        {/* Metadata Grid - indented to align with name (past status dot) */}
        <div className="mt-4 grid grid-cols-2 gap-4 pl-6 sm:grid-cols-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              IP Address
            </div>
            <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
              {hardware.host}:{hardware.port ?? 80}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Status
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${getStatusColor()}`} />
              <span className="text-sm text-gray-900 dark:text-white">{getStatusText()}</span>
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Devices
            </div>
            <div className="mt-1 text-sm text-gray-900 dark:text-white">
              {devices.length} configured
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Webhook
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className={`text-sm ${webhookStatus.color}`}>{webhookStatus.text}</span>
              {hardware.webhookEnabled && (
                <button
                  onClick={onShowWebhookDetails}
                  className="rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300"
                  title="Show webhook details"
                  type="button"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expand/Collapse Toggle - indented to align with name */}
        <button
          onClick={onToggleExpand}
          className="mt-4 flex items-center gap-1.5 pl-[22px] text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          type="button"
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
          {isExpanded ? 'Hide devices' : 'Show devices'}
        </button>
      </div>

      {/* Expanded Content - Devices */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 dark:border-white/[0.06] dark:bg-zinc-900/50">
          {devicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
            </div>
          ) : (
            <>
              {devices.length === 0 ? (
                <div className="p-6 text-center">
                  <Power className="mx-auto h-8 w-8 text-gray-400 dark:text-zinc-600" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                    No devices configured yet. Add a device to control this hardware.
                  </p>
                  <Button
                    className="mt-4"
                    size="sm"
                    onClick={() => setAddingDevice(true)}
                    leftIcon={<Plus className="h-4 w-4" />}
                  >
                    Add Device
                  </Button>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
                    {devices.map((device) => (
                      <DeviceListItem
                        key={device.id}
                        device={device}
                        onDisable={() => setDisablingDevice(device)}
                      />
                    ))}
                  </div>
                  <div className="border-t border-gray-200 p-3 dark:border-white/[0.06]">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAddingDevice(true)}
                      leftIcon={<Plus className="h-4 w-4" />}
                    >
                      Add Device
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Add Device Modal */}
      {addingDevice && (
        <AddDeviceModal
          isOpen={true}
          hardware={hardware}
          onClose={() => setAddingDevice(false)}
          onSuccess={() => {
            setAddingDevice(false);
            invalidateDevices();
          }}
        />
      )}

      {/* Disable Device Modal */}
      {disablingDevice && (
        <DisableDeviceModal
          isOpen={true}
          device={disablingDevice}
          onClose={() => setDisablingDevice(null)}
          onSuccess={() => {
            setDisablingDevice(null);
            invalidateDevices();
          }}
        />
      )}
    </div>
  );
}

interface DeviceListItemProps {
  device: ShellyDeviceResponse;
  onDisable: () => void;
}

function DeviceListItem({ device, onDisable }: DeviceListItemProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-3">
        <Power className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">{device.name}</div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
            <span>{device.deviceType}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{device.channelType}</span>
            {device.channelIndex !== undefined && device.channelIndex > 0 && (
              <>
                <span className="text-gray-300 dark:text-zinc-700">•</span>
                <span>Channel {device.channelIndex}</span>
              </>
            )}
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span className="font-mono">{device.id}</span>
          </div>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onDisable} title="Remove device">
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  );
}

interface DevicesTabProps {
  hardware: ShellyHardwareResponse[];
  onNavigateToHardware: (hardwareId: string) => void;
}

function DevicesTab({ hardware, onNavigateToHardware }: DevicesTabProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiIntegrationsShellyDevices();
  const deleteMutation = useDeleteApiIntegrationsShellyDevicesId();

  const devices = data?.devices ?? [];

  // Create a map of hardware ID to hardware for quick lookup
  const hardwareMap = new Map(hardware.map((hw) => [hw.id, hw]));

  const invalidateDevices = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsShellyDevicesQueryKey() });
    // Also invalidate per-hardware device lists
    hardware.forEach((hw) => {
      if (hw.id) {
        queryClient.invalidateQueries({
          queryKey: getGetApiIntegrationsShellyHardwareIdDevicesQueryKey(hw.id),
        });
      }
    });
  };

  const handleDelete = async (deviceId: string) => {
    try {
      await deleteMutation.mutateAsync({ id: deviceId });
      invalidateDevices();
    } catch {
      // Error handling done by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h3 className="font-medium text-red-400">Unable to fetch devices</h3>
            <p className="mt-1 text-sm text-red-400/70">
              There was an error loading your device list.
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
        >
          Retry
        </button>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
        <Power className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
        <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
          No devices configured
        </h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
          Add devices from the Hardware tab by expanding a hardware card
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      {/* Table header */}
      <div className="hidden border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-white/[0.06] dark:bg-zinc-900/50 sm:grid sm:grid-cols-12 sm:gap-4">
        <div className="col-span-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Name
        </div>
        <div className="col-span-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Type
        </div>
        <div className="col-span-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Channel
        </div>
        <div className="col-span-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Hardware
        </div>
        <div className="col-span-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Device ID
        </div>
      </div>

      {/* Device rows */}
      <div className="divide-y divide-gray-200 dark:divide-white/[0.06]">
        {devices.map((device) => {
          const parentHardware = device.hardwareId ? hardwareMap.get(device.hardwareId) : null;
          return (
            <div
              key={device.id}
              className="flex flex-col gap-2 px-4 py-3 sm:grid sm:grid-cols-12 sm:items-center sm:gap-4"
            >
              {/* Name */}
              <div className="col-span-3">
                <div className="flex items-center gap-2">
                  <Power className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-zinc-500" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {device.name}
                  </span>
                </div>
              </div>

              {/* Type */}
              <div className="col-span-2">
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                  {device.deviceType}
                </span>
              </div>

              {/* Channel */}
              <div className="col-span-2 text-sm text-gray-500 dark:text-zinc-500">
                {device.channelType}
                {device.channelIndex !== undefined && device.channelIndex > 0 && (
                  <span className="ml-1">#{device.channelIndex}</span>
                )}
              </div>

              {/* Hardware */}
              <div className="col-span-3">
                {parentHardware ? (
                  <button
                    onClick={() => device.hardwareId && onNavigateToHardware(device.hardwareId)}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {parentHardware.name}
                  </button>
                ) : (
                  <span className="text-sm text-gray-400 dark:text-zinc-600">Unknown</span>
                )}
              </div>

              {/* Device ID + Actions */}
              <div className="col-span-2 flex items-center justify-between gap-2">
                <code className="truncate text-xs text-gray-500 dark:text-zinc-500">
                  {device.id}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => device.id && handleDelete(device.id)}
                  disabled={deleteMutation.isPending}
                  title="Remove device"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface AddHardwareModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddHardwareFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function AddHardwareModal({ isOpen, onClose, onSubmit, isLoading, error }: AddHardwareModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<AddHardwareFormData>({
    resolver: zodResolver(addHardwareSchema),
    defaultValues: {
      port: 80,
      webhookEnabled: true,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-hardware-form" loading={isLoading}>
        Add Hardware
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Shelly Hardware"
      subtitle="Connect a new Shelly device to your smart home"
      size="md"
      footer={footerContent}
    >
      <form id="add-hardware-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room Switch"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="IP Address"
          placeholder="192.168.1.100"
          error={errors.host?.message}
          {...register('host')}
        />

        <TextField
          label="Port"
          placeholder="80"
          type="number"
          error={errors.port?.message}
          {...register('port')}
        />

        <TextField
          label="Username (optional)"
          placeholder="admin"
          error={errors.username?.message}
          {...register('username')}
        />

        <PasswordField
          label="Password (optional)"
          placeholder="Enter password"
          error={errors.password?.message}
          {...register('password')}
        />

        <Controller
          name="webhookEnabled"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              label="Enable webhooks"
              description="Receive real-time state updates from the device"
              checked={value ?? false}
              onChange={onChange}
            />
          )}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface EditHardwareModalProps {
  isOpen: boolean;
  hardware: ShellyHardwareResponse;
  onClose: () => void;
  onSubmit: (data: EditHardwareFormData) => Promise<void>;
  isLoading: boolean;
}

function EditHardwareModal({
  isOpen,
  hardware,
  onClose,
  onSubmit,
  isLoading,
}: EditHardwareModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EditHardwareFormData>({
    resolver: zodResolver(editHardwareSchema),
    defaultValues: {
      name: hardware.name ?? '',
      host: hardware.host ?? '',
      port: hardware.port ?? 80,
      username: hardware.username ?? '',
      webhookEnabled: hardware.webhookEnabled ?? false,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-hardware-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Hardware"
      subtitle="Update Shelly device connection settings"
      size="md"
      footer={footerContent}
    >
      <form id="edit-hardware-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room Switch"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="IP Address"
          placeholder="192.168.1.100"
          error={errors.host?.message}
          {...register('host')}
        />

        <TextField
          label="Port"
          placeholder="80"
          type="number"
          error={errors.port?.message}
          {...register('port')}
        />

        <TextField
          label="Username (optional)"
          placeholder="admin"
          error={errors.username?.message}
          {...register('username')}
        />

        <PasswordField
          label="Password (optional)"
          placeholder="Enter new password"
          error={errors.password?.message}
          {...register('password')}
        />

        <Controller
          name="webhookEnabled"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              label="Enable webhooks"
              description="Receive real-time state updates from the device"
              checked={value ?? false}
              onChange={onChange}
            />
          )}
        />
      </form>
    </Modal>
  );
}

interface DeleteHardwareModalProps {
  isOpen: boolean;
  hardware: ShellyHardwareResponse;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteHardwareModal({
  isOpen,
  hardware,
  onClose,
  onConfirm,
  isLoading,
}: DeleteHardwareModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Hardware"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{hardware.name}</strong>? This will also
        remove all associated devices.
      </p>
    </Modal>
  );
}

interface AddDeviceModalProps {
  isOpen: boolean;
  hardware: ShellyHardwareResponse;
  onClose: () => void;
  onSuccess: () => void;
}

function AddDeviceModal({ isOpen, hardware, onClose, onSuccess }: AddDeviceModalProps) {
  const createMutation = usePostApiIntegrationsShellyDevices();
  const { data: capabilitiesData, isLoading: capabilitiesLoading } =
    useGetApiIntegrationsShellyHardwareIdCapabilities(hardware.id ?? '', {
      query: { enabled: !!hardware.id },
    });
  const [error, setError] = useState<string | null>(null);

  const capabilities = capabilitiesData?.capabilities;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<AddDeviceFormData>({
    resolver: zodResolver(addDeviceSchema),
    defaultValues: {
      channelIndex: 0,
    },
  });

  const selectedDeviceType = watch('deviceType');
  const selectedChannelType = watch('channelType');

  // Get available types from capabilities
  const channels = capabilities?.channels;
  const deviceTypes = getDeviceTypesFromCapabilities(channels);
  const channelTypes = getChannelTypesFromCapabilities(channels);
  const availableChannels = getAvailableChannels(channels, selectedChannelType);

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = async (data: AddDeviceFormData) => {
    setError(null);
    try {
      await createMutation.mutateAsync({
        data: {
          hardwareId: hardware.id ?? '',
          id: data.id,
          name: data.name,
          deviceType: data.deviceType as ShellyCreateDeviceRequestDeviceType,
          channelType: data.channelType as ShellyCreateDeviceRequestChannelType,
          channelIndex: data.channelIndex,
        },
      });
      handleClose();
      onSuccess();
    } catch (err) {
      const apiError = err as { code?: string; message?: string };
      if (apiError.code === 'DEVICE_ID_EXISTS') {
        setError('This ID is already in use. Please choose a different one.');
      } else {
        setError(apiError.message ?? 'Failed to add device. Please try again.');
      }
    }
  };

  const footerContent = (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={handleClose}
        disabled={createMutation.isPending}
      >
        Cancel
      </Button>
      <Button type="submit" form="add-device-form" loading={createMutation.isPending}>
        Add Device
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Device"
      subtitle={`Create a device for ${hardware.name}`}
      size="md"
      footer={footerContent}
    >
      {capabilitiesLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      ) : (
        <form id="add-device-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <TextField
            label="Name"
            placeholder="Living Room Light"
            error={errors.name?.message}
            {...register('name')}
          />

          <TextField
            label="Device ID"
            placeholder="living-room-light"
            error={errors.id?.message}
            {...register('id')}
          />

          <Controller
            name="deviceType"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DropdownSelect
                label="Device Type"
                value={value ?? null}
                onChange={(newValue) => {
                  onChange(newValue ?? '');
                  setValue('channelType', '');
                  setValue('channelIndex', 0);
                }}
                options={deviceTypes.map((type) => ({
                  value: type,
                  label: getDeviceTypeLabel(type as DevicesDeviceType),
                }))}
                placeholder="Select device type..."
                error={errors.deviceType?.message}
              />
            )}
          />

          {selectedDeviceType && (
            <Controller
              name="channelType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DropdownSelect
                  label="Channel Type"
                  value={value ?? null}
                  onChange={(newValue) => {
                    onChange(newValue ?? '');
                    setValue('channelIndex', 0);
                  }}
                  options={channelTypes.map((type) => ({
                    value: type,
                    label: type.charAt(0).toUpperCase() + type.slice(1),
                  }))}
                  placeholder="Select channel type..."
                  error={errors.channelType?.message}
                />
              )}
            />
          )}

          {selectedChannelType && availableChannels.length > 1 && (
            <Controller
              name="channelIndex"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DropdownSelect
                  label="Output Channel"
                  value={value ?? 0}
                  onChange={(newValue) => onChange(newValue ?? 0)}
                  options={availableChannels.map((channel) => ({
                    value: channel.index,
                    label:
                      channel.name ||
                      `${selectedChannelType.charAt(0).toUpperCase() + selectedChannelType.slice(1)} ${channel.index}`,
                  }))}
                  placeholder="Select output channel..."
                  hint="Select which output to control with this device"
                />
              )}
            />
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
              {error}
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}

interface DisableDeviceModalProps {
  isOpen: boolean;
  device: ShellyDeviceResponse;
  onClose: () => void;
  onSuccess: () => void;
}

function DisableDeviceModal({ isOpen, device, onClose, onSuccess }: DisableDeviceModalProps) {
  const deleteMutation = useDeleteApiIntegrationsShellyDevicesId();

  const handleConfirm = async () => {
    if (!device.id) return;
    try {
      await deleteMutation.mutateAsync({ id: device.id });
      onClose();
      onSuccess();
    } catch {
      // Error handling done by mutation
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={deleteMutation.isPending}>
        Cancel
      </Button>
      <Button
        type="button"
        variant="danger"
        onClick={handleConfirm}
        loading={deleteMutation.isPending}
      >
        Remove
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Remove Device"
      subtitle="Remove device from your smart home"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to remove{' '}
        <strong className="text-gray-900 dark:text-white">{device.name}</strong>? You can re-add it
        later.
      </p>
    </Modal>
  );
}

// Custom dropdown component
interface DropdownSelectProps {
  label: string;
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  options: { value: string | number; label: string; sublabel?: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
}

function DropdownSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled,
  error,
  hint,
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          disabled && 'cursor-not-allowed opacity-50',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value || null);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === option.value && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === option.value && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {option.label}
                  </div>
                  {option.sublabel && (
                    <div className="text-xs text-gray-500 dark:text-zinc-500">
                      {option.sublabel}
                    </div>
                  )}
                </div>
                {value === option.value && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
      {hint && !error && <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">{hint}</p>}
    </div>
  );
}

// Settings Tab Component
function SettingsTab() {
  const queryClient = useQueryClient();
  const { data: settingsData, isLoading, isError, refetch } = useGetApiIntegrationsShellySettings();
  const updateMutation = usePutApiIntegrationsShellySettings();

  const [webhookHost, setWebhookHost] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize form value when settings are loaded
  useEffect(() => {
    if (settingsData?.webhookHost !== undefined) {
      setWebhookHost(settingsData.webhookHost);
    }
  }, [settingsData?.webhookHost]);

  const handleWebhookHostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebhookHost(e.target.value);
    setHasChanges(e.target.value !== (settingsData?.webhookHost ?? ''));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ data: { webhookHost } });
      queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsShellySettingsQueryKey() });
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // Error handling done by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h3 className="font-medium text-red-400">Unable to fetch settings</h3>
            <p className="mt-1 text-sm text-red-400/70">
              There was an error loading the integration settings.
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Webhook Configuration */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800">
            <Webhook className="h-5 w-5 text-gray-500 dark:text-zinc-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Webhook Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
              Configure the webhook URL that Shelly devices will use to send state updates to this
              server.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Webhook Host
            </label>
            <input
              type="text"
              value={webhookHost}
              onChange={handleWebhookHostChange}
              placeholder="http://192.168.1.100:8080"
              className={cn(
                'w-full rounded-lg border px-3 py-2.5 text-sm transition-all',
                'bg-white dark:bg-[#111113]',
                'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
                'text-gray-900 dark:text-white',
                'placeholder:text-gray-400 dark:placeholder:text-zinc-500',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            />
            <p className="mt-1.5 text-xs text-gray-500 dark:text-zinc-500">
              This is the base URL that Shelly devices will use to send webhook notifications. It
              should be the IP address or hostname of this server, accessible from your Shelly
              devices.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} loading={updateMutation.isPending} disabled={!hasChanges}>
              Save Changes
            </Button>
            {saveSuccess && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <Check className="h-4 w-4" />
                Settings saved
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-6 dark:bg-blue-500/10">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/20">
            <Settings className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h3 className="font-medium text-blue-700 dark:text-blue-400">About Webhooks</h3>
            <p className="mt-1 text-sm text-blue-600/80 dark:text-blue-400/70">
              Webhooks allow Shelly devices to push state changes to this server in real-time. When
              a device state changes (e.g., a switch is toggled), the device will send a
              notification to the configured webhook URL. The server will then update the device
              state accordingly.
            </p>
            <p className="mt-2 text-sm text-blue-600/80 dark:text-blue-400/70">
              Make sure your Shelly devices can reach this server at the configured webhook host
              address.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper functions for capabilities
function getDeviceTypesFromCapabilities(channels: ShellyChannelCapability[] | undefined): string[] {
  // Return available device types based on channel types
  // Map channel types to device types
  const deviceTypes = new Set<string>();
  if (!channels || channels.length === 0) {
    // Default device types
    return Object.values(ShellyCreateDeviceRequestDeviceType);
  }
  channels.forEach((channel) => {
    if (channel.type === 'relay') {
      deviceTypes.add('switch');
      deviceTypes.add('light');
    } else if (channel.type === 'dimmer') {
      deviceTypes.add('light');
    } else if (channel.type === 'roller') {
      deviceTypes.add('window_covering');
    } else if (channel.type === 'rgbw') {
      deviceTypes.add('light');
    }
  });
  return Array.from(deviceTypes);
}

function getChannelTypesFromCapabilities(
  channels: ShellyChannelCapability[] | undefined
): string[] {
  if (!channels || channels.length === 0) {
    return Object.values(ShellyCreateDeviceRequestChannelType);
  }
  const types = new Set<string>();
  channels.forEach((channel) => {
    if (channel.type) {
      types.add(channel.type);
    }
  });
  return Array.from(types);
}

interface AvailableChannel {
  index: number;
  name: string | undefined;
  type: string | undefined;
}

function getAvailableChannels(
  channels: ShellyChannelCapability[] | undefined,
  channelType: string
): AvailableChannel[] {
  if (!channels || channels.length === 0 || !channelType) {
    return [{ index: 0, name: undefined, type: channelType }];
  }
  const matchingChannels = channels.filter((c) => c.type === channelType);
  if (matchingChannels.length === 0) {
    return [{ index: 0, name: undefined, type: channelType }];
  }
  return matchingChannels.map((c) => ({
    index: c.index ?? 0,
    name: c.name,
    type: c.type,
  }));
}

// Webhook display helpers
const EVENT_LABELS: Record<string, string> = {
  // Gen1 relay events
  out_on_url: 'Output On',
  out_off_url: 'Output Off',
  // Gen1 roller events
  roller_open_url: 'Roller Open',
  roller_close_url: 'Roller Close',
  roller_stop_url: 'Roller Stop',
  // Gen2 switch events
  'switch.on': 'Switch On',
  'switch.off': 'Switch Off',
  // Gen2 cover events
  'cover.open': 'Cover Open',
  'cover.closed': 'Cover Closed',
  'cover.stopped': 'Cover Stopped',
  // Gen2 light events
  'light.on': 'Light On',
  'light.off': 'Light Off',
};

function formatEventName(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

function isOurWebhook(url: string): boolean {
  // Check if this URL points to our webhook endpoint
  return url.includes('/api/integrations/shelly/webhook/');
}

// Webhook Details Panel Component
interface WebhookDetailsPanelProps {
  hardware: ShellyHardwareResponse | null;
  onClose: () => void;
}

function WebhookDetailsPanel({ hardware, onClose }: WebhookDetailsPanelProps) {
  const { data: statusData, isLoading } = useGetApiIntegrationsShellyHardwareIdWebhookStatus(
    hardware?.id ?? '',
    { query: { enabled: !!hardware?.id && !!hardware?.webhookEnabled } }
  );

  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const handleCopyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  if (!hardware) return null;

  const expectedWebhooks = statusData?.expectedWebhooks ?? [];
  const allDeviceWebhooks = statusData?.allDeviceWebhooks ?? [];
  const summary = statusData?.summary;

  return (
    <SlideOutPanel
      isOpen={true}
      onClose={onClose}
      title="Webhook Configuration"
      subtitle={hardware.name}
      size="xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary */}
          {summary && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/[0.06] dark:bg-zinc-900/50">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      'h-3 w-3 rounded-full',
                      summary.fullyConfigured ? 'bg-emerald-500' : 'bg-amber-500'
                    )}
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {summary.fullyConfigured ? 'Fully Configured' : 'Needs Attention'}
                  </span>
                </div>
                <div className="flex gap-4 text-xs text-gray-500 dark:text-zinc-500">
                  <span>
                    <span className="text-emerald-500">{summary.correctCount}</span> correct
                  </span>
                  {(summary.wrongUrlCount ?? 0) > 0 && (
                    <span>
                      <span className="text-amber-500">{summary.wrongUrlCount}</span> wrong URL
                    </span>
                  )}
                  {(summary.missingCount ?? 0) > 0 && (
                    <span>
                      <span className="text-red-500">{summary.missingCount}</span> missing
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Expected Webhooks Status */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Expected Webhooks
            </h3>
            {expectedWebhooks.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-500">
                No webhooks expected for this device.
              </p>
            ) : (
              <div className="space-y-2">
                {expectedWebhooks.map((webhook, index) => (
                  <ExpectedWebhookRow
                    key={index}
                    webhook={webhook}
                    copiedUrl={copiedUrl}
                    onCopy={handleCopyUrl}
                  />
                ))}
              </div>
            )}
          </div>

          {/* All Device Webhooks */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              All Webhooks on Device
            </h3>
            {allDeviceWebhooks.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-500">
                No webhooks configured on device.
              </p>
            ) : (
              <div className="space-y-2">
                {allDeviceWebhooks.map((webhook, index) => (
                  <DeviceWebhookRow
                    key={index}
                    webhook={webhook}
                    copiedUrl={copiedUrl}
                    onCopy={handleCopyUrl}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SlideOutPanel>
  );
}

interface ExpectedWebhookRowProps {
  webhook: ShellyExpectedWebhookStatus;
  copiedUrl: string | null;
  onCopy: (url: string) => void;
}

function ExpectedWebhookRow({ webhook, copiedUrl, onCopy }: ExpectedWebhookRowProps) {
  const statusColors = {
    correct: 'bg-emerald-500',
    wrong_url: 'bg-amber-500',
    missing: 'bg-red-500',
  };
  const statusLabels = {
    correct: 'Correct',
    wrong_url: 'Wrong URL',
    missing: 'Missing',
  };
  const status = (webhook.status ?? 'missing') as keyof typeof statusColors;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/[0.06] dark:bg-zinc-800/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn('h-2 w-2 rounded-full', statusColors[status])} />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Channel {webhook.channel}: {webhook.eventLabel ?? webhook.event}
          </span>
        </div>
        <span
          className={cn(
            'text-xs font-medium',
            status === 'correct'
              ? 'text-emerald-500'
              : status === 'wrong_url'
                ? 'text-amber-500'
                : 'text-red-500'
          )}
        >
          {statusLabels[status]}
        </span>
      </div>

      {/* Expected URL */}
      <div className="mt-2">
        <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-zinc-600">
          Expected
        </div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700 dark:bg-zinc-900 dark:text-zinc-300">
            {webhook.expectedUrl}
          </code>
          <button
            onClick={() => webhook.expectedUrl && onCopy(webhook.expectedUrl)}
            className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
            title="Copy URL"
            type="button"
          >
            {copiedUrl === webhook.expectedUrl ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* Actual URL (if different or wrong) */}
      {webhook.actualUrl && status !== 'correct' && (
        <div className="mt-2">
          <div className="text-[10px] font-medium uppercase tracking-wider text-amber-500">
            Currently Configured
          </div>
          <code className="mt-1 block overflow-x-auto whitespace-nowrap rounded bg-amber-50 px-2 py-1 font-mono text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
            {webhook.actualUrl}
          </code>
        </div>
      )}
    </div>
  );
}

interface DeviceWebhookRowProps {
  webhook: ShellyWebhookInfo;
  copiedUrl: string | null;
  onCopy: (url: string) => void;
}

function DeviceWebhookRow({ webhook, copiedUrl, onCopy }: DeviceWebhookRowProps) {
  const url = webhook.urls?.[0] ?? '';
  const isOurs = isOurWebhook(url);

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        isOurs
          ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-500/20 dark:bg-emerald-500/5'
          : 'border-gray-200 bg-white dark:border-white/[0.06] dark:bg-zinc-800/50'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Channel {webhook.channel}: {formatEventName(webhook.event ?? '')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!webhook.enabled && (
            <span className="text-xs text-gray-400 dark:text-zinc-600">disabled</span>
          )}
          {isOurs ? (
            <span className="text-xs font-medium text-emerald-500">our webhook</span>
          ) : (
            <span className="text-xs font-medium text-gray-400 dark:text-zinc-500">external</span>
          )}
        </div>
      </div>

      {/* Full URL */}
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700 dark:bg-zinc-900 dark:text-zinc-300">
          {url}
        </code>
        <button
          onClick={() => onCopy(url)}
          className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
          title="Copy URL"
          type="button"
        >
          {copiedUrl === url ? (
            <Check className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}
